import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { X } from 'lucide-react';
import api from '../../services/api';
import { FEEDBACK_STATUS_LABELS } from '../../types/feedback.types';
import type { FeedbackItemAdmin, FeedbackStatus, UpdateFeedbackPayload } from '../../types/feedback.types';

interface Props {
    feedback: FeedbackItemAdmin;
    onClose: () => void;
}

const FeedbackDetailModal: React.FC<Props> = ({ feedback, onClose }) => {
    const queryClient = useQueryClient();
    const modalRef = useRef<HTMLDivElement>(null);
    const [status, setStatus] = useState<FeedbackStatus>(feedback.status);
    const [adminNotes, setAdminNotes] = useState(feedback.admin_notes || '');
    const [error, setError] = useState('');

    const handleClose = useCallback(() => { onClose(); }, [onClose]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                handleClose();
            }
        };
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [handleClose]);

    const mutation = useMutation(
        (data: UpdateFeedbackPayload) =>
            api.feedback.updateFeedback(feedback.id, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['feedback-stats']);
                queryClient.invalidateQueries(['admin-feedback']);
                onClose();
            },
            onError: (err: unknown) => {
                setError((err as any)?.response?.data?.error || 'Failed to save changes. Please try again.');
            },
        }
    );

    const handleSave = () => {
        mutation.mutate({ status, admin_notes: adminNotes });
    };

    return (
        <div className="feedback-modal-overlay">
            <div ref={modalRef} className="feedback-modal" role="dialog" aria-labelledby="feedback-modal-title" aria-modal="true">
                <div className="feedback-modal-header">
                    <h2 id="feedback-modal-title">{feedback.title}</h2>
                    <button className="feedback-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="feedback-modal-body">
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                        <span className={`feedback-type-badge ${feedback.type}`}>{feedback.type}</span>
                        <span className={`feedback-status-badge ${feedback.status}`}>
                            {FEEDBACK_STATUS_LABELS[feedback.status]}
                        </span>
                    </div>

                    <div className="feedback-modal-field">
                        <span className="feedback-modal-label">Submitted By</span>
                        <p>{feedback.submitted_by}</p>
                    </div>

                    <div className="feedback-modal-field">
                        <span className="feedback-modal-label">Date</span>
                        <p>{new Date(feedback.created_at).toLocaleString()}</p>
                    </div>

                    {feedback.rating && (
                        <div className="feedback-modal-field">
                            <span className="feedback-modal-label">Rating</span>
                            <p style={{ color: '#F59E0B', fontSize: '18px' }}>
                                {'\u2605'.repeat(feedback.rating)}{'\u2606'.repeat(5 - feedback.rating)}
                            </p>
                        </div>
                    )}

                    <div className="feedback-modal-field">
                        <span className="feedback-modal-label">Description</span>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{feedback.description}</p>
                    </div>

                    {feedback.page_url && (
                        <div className="feedback-modal-field">
                            <span className="feedback-modal-label">Page URL</span>
                            <p style={{ wordBreak: 'break-all', fontSize: '13px' }}>{feedback.page_url}</p>
                        </div>
                    )}

                    {feedback.browser_info && (
                        <div className="feedback-modal-field">
                            <span className="feedback-modal-label">Browser Info</span>
                            <p style={{ wordBreak: 'break-all', fontSize: '12px', color: '#9ca3af' }}>
                                {feedback.browser_info}
                            </p>
                        </div>
                    )}

                    <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '20px 0' }} />

                    <div className="feedback-modal-field">
                        <label htmlFor="feedback-modal-status">Status</label>
                        <select id="feedback-modal-status" value={status} onChange={(e) => setStatus(e.target.value as FeedbackStatus)}>
                            {Object.entries(FEEDBACK_STATUS_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="feedback-modal-field">
                        <label htmlFor="feedback-modal-notes">Admin Notes</label>
                        <textarea
                            id="feedback-modal-notes"
                            value={adminNotes}
                            onChange={(e) => setAdminNotes(e.target.value)}
                            placeholder="Add notes or response for the user..."
                            rows={4}
                        />
                    </div>
                </div>

                {error && <div className="feedback-error" style={{ color: '#ef4444', marginBottom: '12px' }}>{error}</div>}

                <div className="feedback-modal-actions">
                    <button className="feedback-modal-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="feedback-modal-save"
                        onClick={handleSave}
                        disabled={mutation.isLoading}
                    >
                        {mutation.isLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FeedbackDetailModal;
